import React, { useEffect, useRef } from "react";
import { Info, Copy, ExternalLink } from "lucide-react";

/** Floating native-looking context menu. Closes on outside click / Esc. */
export default function TagContextMenu({ tag, x, y, onExplore, onCopy, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("mousedown", onDown, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  if (!tag) return null;

  // Clamp within viewport
  const vw = window.innerWidth, vh = window.innerHeight;
  const menuW = 240, menuH = 140;
  const left = Math.min(x, vw - menuW - 8);
  const top = Math.min(y, vh - menuH - 8);

  return (
    <div
      ref={ref}
      className="fade-in"
      style={{
        position: "fixed", left, top, width: menuW, zIndex: 80,
        background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)",
        boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
      }}
      data-testid="tag-context-menu"
      role="menu"
    >
      <div style={{ padding: "8px 12px", borderBottom: "1px solid var(--pf-border)", background: "var(--pf-blueprint)" }}>
        <div className="pf-overline" style={{ fontSize: 9 }}>TAG</div>
        <div className="pf-mono" style={{ fontSize: 13, color: "var(--pf-accent)" }} data-testid="tag-context-menu-tag">{tag}</div>
      </div>
      <button className="tag-ctx-item" onClick={onExplore} data-testid="ctx-explore-details">
        <Info size={14} /> Explore Details
      </button>
      <button className="tag-ctx-item" onClick={onCopy} data-testid="ctx-copy-tag">
        <Copy size={14} /> Copy tag
      </button>
      <a
        className="tag-ctx-item"
        href={`https://www.google.com/search?q=${encodeURIComponent(tag + " datasheet")}`}
        target="_blank" rel="noreferrer"
        onClick={onClose}
        data-testid="ctx-find-datasheet"
        style={{ textDecoration: "none" }}
      >
        <ExternalLink size={14} /> Search datasheet
      </a>
      <style>{`
        .tag-ctx-item {
          display: flex; align-items: center; gap: 10px;
          width: 100%;
          padding: 9px 12px;
          background: transparent; border: none;
          color: var(--pf-text);
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 13px;
          text-align: left;
          cursor: pointer;
          transition: background 0.12s ease, color 0.12s ease;
        }
        .tag-ctx-item:hover { background: rgba(255,176,0,0.1); color: var(--pf-accent); }
      `}</style>
    </div>
  );
}
